import React from "react";
import styled, { css } from "styled-components";

import { SubProductAction } from "features/product/types/actions.subproduct";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import { SubProductEditMode } from "./modals/subProductEditModal";
import { ModalName } from "features/product/types/modals";
import { emptyTextPropertyColumn } from "features/product/constants/templates";
import generateRandomId from "features/shared/functions/generateRandomId";

const TableMenu: React.FC = () => {
    const { locale, groupingMode, readOnlyMode } = useSelector(
        (state) => state.subProducts
    );
    const dispatch = useDispatch();

    const handleToggleLocale = () => {
        dispatch({
            type: SubProductAction.TOGGLE_LOCALE_SHOWN,
        });
    };

    const handleToggleGroupingMode = () => {
        dispatch({
            type: SubProductAction.SET_GROUPING_MODE,
            groupingMode: !groupingMode,
        });
    };

    return (
        <Container onClick={(e) => e.stopPropagation()}>
            <ButtonGroup>
                {!readOnlyMode && (
                    <>
                        <FloatingButton
                            onClick={() =>
                                dispatch({
                                    type: SubProductAction.OPEN_MODAL,
                                    modal: {
                                        id: ModalName.SubProductModal,
                                        params: {
                                            mode: SubProductEditMode.Add,
                                        },
                                    },
                                })
                            }
                            title="Add new sub-product"
                        >
                            <span>+</span>
                            <span>SUBPRODUCT</span>
                        </FloatingButton>
                        <FloatingButton
                            onClick={() =>
                                dispatch({
                                    type: SubProductAction.ASSIGN_PROPERTY,
                                    propertyColumn: {
                                        ...emptyTextPropertyColumn,
                                        accessor: generateRandomId(),
                                    },
                                })
                            }
                            title="Add new property"
                        >
                            <span>+</span>
                            <span>PROPERTY</span>
                        </FloatingButton>
                    </>
                )}
            </ButtonGroup>

            <ButtonGroup>
                <FloatingButton
                    onClick={handleToggleLocale}
                    title={`Change to ${locale === "en" ? "Thai" : "English"}`}
                >
                    <span>TOGGLE</span>
                    <span>{(locale === "en" ? "th" : "en").toUpperCase()}</span>
                </FloatingButton>
                {!readOnlyMode && (
                    <FloatingButton
                        onClick={() => handleToggleGroupingMode()}
                        title="Toggle grouping mode"
                    >
                        <span>GROUPING</span>
                        <span>VIEW</span>
                    </FloatingButton>
                )}
            </ButtonGroup>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    justify-content: space-between;

    padding: 0.5rem;
    padding-bottom: 0;
`;

const DisabledCSS = css`
    cursor: initial;
    color: ${(props) => props.theme.colors.neutral[300]};
    background-color: ${(props) => props.theme.colors.neutral[200]};
    &:hover {
        background-color: ${(props) => props.theme.colors.neutral[200]};
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 0.25rem;
`;

const FloatingButton = styled.div<{ isDisabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;

    min-width: 6rem;
    height: 1.8rem;

    padding: 0 0.5rem;

    border-radius: 0.2rem;
    border-right: solid 0.5px ${(props) => props.theme.colors.neutral[200]};

    font-weight: ${(props) => props.theme.fontWeight.bolder};
    font-size: 0.8rem;

    color: ${(props) => props.theme.colors.neutral[700]};
    background: ${(props) => props.theme.colors.neutral[200]};

    cursor: pointer;
    user-select: none;

    &:hover {
        background: ${(props) => props.theme.colors.neutral[100]};
    }

    &:last-child {
        border-right: none;
    }

    & > *:first-child {
        margin-right: 5px;
    }

    ${(props) => props.isDisabled && DisabledCSS};
`;

export default TableMenu;
